import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { text, brandAltBackground, neutral, } from '@guardian/src-foundations/palette';
import { headline, textSans } from '@guardian/src-foundations/typography';
import { from } from '@guardian/src-foundations/mq';
import { Special } from '@guardian/types';
import ArrowInCircle from '@frontend/static/icons/arrow-in-circle.svg';
import { decidePalette } from '@root/src/web/lib/decidePalette';
import { StarRating } from '@root/src/web/components/StarRating/StarRating';
import { QuoteIcon } from '@root/src/web/components/QuoteIcon';
import { Hide } from '@root/src/web/components/Hide';
import { Avatar } from '@frontend/web/components/Avatar';
const neutralBackground = css `
	background-color: ${neutral[97]};
	a {
		color: inherit;
	}
	:hover {
		background-color: ${neutral[93]};
	}
`;
const pillarBackground = (palette) => {
    return css `
		background-color: ${palette.background.richLink};
	`;
};
const textColour = (palette) => {
    return css `
		color: ${palette.text.richLink};
	`;
};
const richLinkTopBorder = (palette) => {
    return css `
		border-top: 1px;
		border-top-style: solid;
		border-top-color: ${palette.border.richLink};
	`;
};
const richLinkLink = css `
	text-decoration: none;
`;
const richLinkElements = css `
	padding-top: 2px;
	padding-right: 5px;
	padding-left: 5px;
	padding-bottom: 5px;
`;
const richLinkHeader = css `
	padding-bottom: 10px;
	color: ${neutral[0]};
`;
const richLinkTitle = css `
	${headline.xxxsmall()};
	font-size: 14px;
	padding-top: 1px;
	padding-bottom: 1px;
	font-weight: 400;
	${from.wide} {
		${headline.xxsmall()};
		padding-bottom: 5px;
	}
`;
const labsRichLinkTitle = css `
	${from.wide} {
		${textSans.medium({ fontWeight: 'bold', lineHeight: 'regular' })}
	}
	${textSans.small({ fontWeight: 'bold', lineHeight: 'regular' })}
`;
const richLinkReadMore = (palette) => {
    return css `
		fill: ${palette.fill.richLink};
		color: ${palette.text.richLink};
		padding-top: 2px;
	`;
};
const readMoreTextStyle = css `
	${headline.xxxsmall()};
	font-size: 14px;
	${from.wide} {
		${headline.xxxsmall()}
		line-height: 25px;
	}
	display: inline-block;
	height: 30px;
	line-height: 25px;
	padding-left: 4px;
	vertical-align: top;
	font-weight: 500;
	text-decoration: none;
`;
const labsReadMoreTextStyle = css `
	${textSans.medium({ fontWeight: 'regular' })}
	display: inline-block;
	height: 30px;
	line-height: 25px;
	padding-left: 4px;
	vertical-align: top;
	text-decoration: none;
	color: ${neutral[7]};
`;
const byline = css `
	${headline.xxxsmall()};
	font-size: 14px;
	font-style: italic;
	${from.wide} {
		${headline.xxsmall()};
	}
`;
const contributorImageWrapper = css `
	width: 5rem;
	height: 5rem;
	margin-left: auto;
	margin-right: 0.3rem;
	${from.wide} {
		width: 8.5rem;
		height: 8.5rem;
	}

	/* TODO remove the default img styling in ArticleBody.tsx - do we need direct element styling? */
	img {
		width: 100%;
		height: 100%;
	}
`;
const paidForBranding = css `
	${textSans.xxsmall()};
	font-weight: bold;
	color: ${text.supporting};
`;
const starWrapper = css `
	background-color: ${brandAltBackground.primary};
	display: inline-block;
`;
const readMoreText = (contentType) => {
    switch (contentType) {
        case 'audio':
            return 'Listen';
        case 'gallery':
            return 'View Gallery';
        case 'video':
            return 'Watch';
        default:
            return 'Read more';
    }
};
const getMainContributor = (tags) => {
    const contributorTags = tags.filter((t) => t.type === 'Contributor');
    return contributorTags.length > 0 ? contributorTags[0].title : '';
};
const imageStyles = css `
	width: 100%;
	height: auto;
`;
export const RichLink = ({ richLinkIndex, cardStyle, imageData, headlineText, contentType, url, starRating, format, tags, sponsorName, contributorImage, isPlaceholder, }) => {
    const palette = decidePalette(format);
    const linkText = cardStyle === 'letters' ? `${headlineText} | Letters ` : headlineText;
    const imageCardStyles = ['news', 'letters', 'media', 'feature'];
    const showImage = imageData &&
        imageData.thumbnailUrl &&
        imageCardStyles.includes(cardStyle);
    const isPaidContent = tags
        ? tags.filter((t) => t.id === 'tone/advertisement-features').length > 0
        : false;
    const isOpinion = cardStyle === 'comment';
    const mainContributor = getMainContributor(tags);
    const isLabs = format.theme === Special.Labs;
    return (_jsx("div", Object.assign({ "data-print-layout": "hide", "data-link-name": `rich-link-${richLinkIndex} | ${richLinkIndex}`, "data-component": "rich-link", css: pillarBackground(palette), "data-name": (isPlaceholder && 'placeholder') || '' }, { children: _jsx("div", Object.assign({ css: neutralBackground }, { children: _jsxs("a", Object.assign({ css: richLinkLink, href: url }, { children: [_jsx("div", { css: richLinkTopBorder(palette) }, void 0), showImage && (_jsx("div", { children: _jsx("img", { css: imageStyles, src: imageData.thumbnailUrl, alt: imageData.altText, width: imageData.width, height: imageData.height }, void 0) }, void 0)), _jsxs("div", Object.assign({ css: richLinkElements }, { children: [_jsxs("div", Object.assign({ css: richLinkHeader }, { children: [_jsxs("div", Object.assign({ css: isLabs ? labsRichLinkTitle : richLinkTitle }, { children: [isOpinion && (_jsxs(_Fragment, { children: [_jsx(Hide, Object.assign({ when: "above", breakpoint: "wide" }, { children: _jsx(QuoteIcon, { colour: palette.fill.quoteIcon, size: "small" }, void 0) }), void 0), _jsx(Hide, Object.assign({ when: "below", breakpoint: "wide" }, { children: _jsx(QuoteIcon, { colour: palette.fill.quoteIcon, size: "medium" }, void 0) }), void 0)] }, void 0)), linkText] }), void 0), isOpinion && (_jsx("div", Object.assign({ css: [byline, textColour(palette)] }, { children: mainContributor }), void 0)), starRating && starRating > 0 && (_jsx("div", Object.assign({ css: starWrapper }, { children: _jsx(StarRating, { rating: starRating, size: "medium" }, void 0) }), void 0)), isPaidContent && sponsorName && (_jsxs("div", Object.assign({ css: paidForBranding }, { children: ["Paid for by ", sponsorName] }), void 0))] }), void 0), isOpinion && contributorImage && (_jsx("div", Object.assign({ css: contributorImageWrapper }, { children: _jsx(Avatar, { imageSrc: contributorImage, imageAlt: mainContributor, palette: decidePalette(format) }, void 0) }), void 0)), _jsxs("div", Object.assign({ css: richLinkReadMore(palette) }, { children: [_jsx(ArrowInCircle, {}, void 0), _jsx("div", Object.assign({ css: isLabs
                                            ? labsReadMoreTextStyle
                                            : readMoreTextStyle }, { children: readMoreText(contentType) }), void 0)] }), void 0)] }), void 0)] }), void 0) }), void 0) }), void 0));
};
